import { PERMISSIONS } from '../constants/permissions';
import { hasPermission } from './permissions';

export const ROLE_HR_ADMIN = 'HR_ADMIN';
export const ROLE_TEAM_LEAD = 'TEAM_LEAD';
export const ROLE_REPORTING_MANAGER = 'REPORTING_MANAGER';

export function hasRole(roles: readonly string[] | undefined, role: string): boolean {
  return roles?.includes(role) === true;
}

export function isHrAdmin(roles: readonly string[] | undefined): boolean {
  return hasRole(roles, ROLE_HR_ADMIN);
}

export function isTeamLead(roles: readonly string[] | undefined): boolean {
  return hasRole(roles, ROLE_TEAM_LEAD);
}

export function isReportingManager(roles: readonly string[] | undefined): boolean {
  return hasRole(roles, ROLE_REPORTING_MANAGER);
}

/**
 * Team Lead dashboard (HR Admin layout, team-scoped data). HR admins keep the
 * organization-wide dashboard, so a lead who can already view all employees
 * does not get the team view.
 */
export function canViewTeamDashboard(
  roles: readonly string[] | undefined,
  permissions: readonly string[],
): boolean {
  return (
    isTeamLead(roles) &&
    hasPermission(permissions, PERMISSIONS.ATTENDANCE_VIEW) &&
    !hasPermission(permissions, PERMISSIONS.EMPLOYEE_VIEW)
  );
}

/** TL / RM / HR with module approve permission — not a plain employee. */
export function canSeeRequestApprovals(
  roles: readonly string[] | undefined,
  permissions: readonly string[],
  approvePermission: string,
): boolean {
  if (!hasPermission(permissions, approvePermission)) return false;
  return (
    isHrAdmin(roles) ||
    isTeamLead(roles) ||
    isReportingManager(roles)
  );
}

export function leaveApprovalListTitle(roles: readonly string[] | undefined): string {
  if (isTeamLead(roles) && !isHrAdmin(roles)) return 'Team Leave Requests';
  return 'Leave Requests';
}

export function wfhApprovalListTitle(roles: readonly string[] | undefined): string {
  if (isTeamLead(roles) && !isHrAdmin(roles)) return 'Team WFH Requests';
  return 'WFH Requests';
}

export function permissionApprovalListTitle(roles: readonly string[] | undefined): string {
  if (isTeamLead(roles) && !isHrAdmin(roles)) return 'Team Permission Requests';
  return 'Team Permission Requests';
}

export function attendanceCorrectionApprovalListTitle(
  roles: readonly string[] | undefined,
): string {
  if (isTeamLead(roles) && !isHrAdmin(roles)) {
    return 'Team Attendance Corrections';
  }
  return 'Attendance Corrections';
}

export const TEAM_APPROVAL_PERMISSIONS = [
  PERMISSIONS.LEAVE_APPROVE,
  PERMISSIONS.WFH_APPROVE,
  PERMISSIONS.PERMISSION_APPROVE,
  PERMISSIONS.ATTENDANCE_APPROVE,
] as const;
